import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiLock,
  FiLogOut,
  FiAlertTriangle,
  FiTrash2,
  FiCheck,
  FiLoader,
  FiEye,
  FiEyeOff,
  FiShield,
  FiRefreshCw,
} from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../../../context/UserContext";
import { createAuthAxios } from "../../../utils/apiUtils";
import { AUTH_ENDPOINTS } from "../../../config/apiConfig";
import { toast } from "react-toastify";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const AccountSettings = () => {
  const { user, logout } = useUserContext();
  const navigate = useNavigate();

  // Detect if user signed in with Google OAuth
  // Backend returns googleId as boolean (true/false)
  const isGoogleUser = user?.googleId === true;

  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const handlePasswordReset = async () => {
    setIsResetting(true);
    try {
      const authAxios = createAuthAxios();
      await authAxios.post("/v1/auth/forgot-password", {
        email: user?.email,
      });

      toast.success("Password reset instructions sent to your email!");
      setShowPasswordReset(false);
    } catch (error) {
      toast.error("Failed to send password reset email. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/signin");
    toast.success("Logged out successfully");
  };

  const handleDeleteAccount = async () => {
    // Validate confirmation text
    if (deleteConfirmText !== "DELETE_MY_ACCOUNT") {
      toast.error('Please type "DELETE_MY_ACCOUNT" to confirm');
      return;
    }

    // Non-Google users need password
    if (!isGoogleUser && !deletePassword) {
      toast.error("Please enter your password to confirm");
      return;
    }

    setIsDeleting(true);
    try {
      const authAxios = createAuthAxios();
      
      // Build payload based on user type
      const payload = {
        confirmDelete: "DELETE_MY_ACCOUNT",
      };
      
      // Only include password for non-Google users
      if (!isGoogleUser) {
        payload.password = deletePassword;
      }

      await authAxios.delete("/v1/auth/delete-account", { data: payload });

      toast.success("Account deleted successfully");
      logout();
      navigate("/");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Incorrect password");
      } else {
        toast.error(error.response?.data?.error || "Failed to delete account");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="stg-content-wrapper"
    >
      {/* Section Header */}
      <div className="stg-section-header">
        <h1 className="stg-section-title">
          <span className="stg-section-title-icon stg-blue">
            <FiUser />
          </span>
          Account Settings
        </h1>
        <p className="stg-section-description">
          Manage your account information, password, and account actions
        </p>
      </div>

      {/* Account Info Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiMail /> Account Information
          </h3>
          {isGoogleUser && (
            <div className="stg-card-actions">
              <span className="stg-status stg-status-info" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <FcGoogle size={16} />
                Signed in with Google
              </span>
            </div>
          )}
        </div>
        <div className="stg-card-body">
          <div className="stg-form-row">
            <div className="stg-form-group">
              <label className="stg-form-label">
                <FiUser /> Name
              </label>
              <input
                type="text"
                value={user?.name || ""}
                className="stg-input"
                disabled
              />
              <span className="stg-form-hint">Edit in Profile Settings</span>
            </div>
            <div className="stg-form-group">
              <label className="stg-form-label">
                <FiMail /> Email Address
              </label>
              <input
                type="email"
                value={user?.email || ""}
                className="stg-input"
                disabled
              />
              <span className="stg-form-hint">Email cannot be changed</span>
            </div>
          </div>

          <div className="stg-form-row">
            <div className="stg-form-group">
              <label className="stg-form-label">
                <FiShield /> Account Role
              </label>
              <input
                type="text"
                value={user?.role || "User"}
                className="stg-input"
                disabled
                style={{ textTransform: "capitalize" }}
              />
            </div>
            <div className="stg-form-group">
              <label className="stg-form-label">
                <FiCheck /> Onboarding Status
              </label>
              <input
                type="text"
                value={user?.onboardingCompleted ? "Completed" : "Pending"}
                className="stg-input"
                disabled
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Password Card - Only show for non-Google users */}
      {isGoogleUser ? (
        <motion.div variants={itemVariants} className="stg-card">
          <div className="stg-card-header">
            <h3 className="stg-card-title">
              <FiLock /> Password
            </h3>
          </div>
          <div className="stg-card-body">
            <div className="stg-alert stg-alert-info stg-mb-0">
              <FcGoogle className="stg-alert-icon" size={24} />
              <div className="stg-alert-content">
                <p className="stg-alert-title">Signed in with Google</p>
                <p className="stg-alert-message">
                  You signed in using your Google account. Password management is 
                  handled through your Google account settings. You can manage your 
                  Google account security at{" "}
                  <a 
                    href="https://myaccount.google.com/security" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: "var(--stg-indigo-500)", textDecoration: "underline" }}
                  >
                    myaccount.google.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div variants={itemVariants} className="stg-card">
          <div className="stg-card-header">
            <h3 className="stg-card-title">
              <FiLock /> Password
            </h3>
          </div>
          <div className="stg-card-body">
            <p style={{ color: "var(--stg-slate-600)", marginBottom: "16px" }}>
              To change your password, we'll send a password reset link to your
              email address. Click the link in the email to create a new password.
            </p>

            {showPasswordReset ? (
              <div
                className="stg-alert stg-alert-info"
                style={{ marginBottom: "16px" }}
              >
                <FiMail className="stg-alert-icon" />
                <div className="stg-alert-content">
                  <p className="stg-alert-title">Confirm Password Reset</p>
                  <p className="stg-alert-message">
                    We'll send a password reset link to:{" "}
                    <strong>{user?.email}</strong>
                  </p>
                </div>
              </div>
            ) : null}

            <div className="stg-flex stg-gap-md">
              {showPasswordReset ? (
                <>
                  <button
                    className="stg-btn stg-btn-secondary"
                    onClick={() => setShowPasswordReset(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="stg-btn stg-btn-primary"
                    onClick={handlePasswordReset}
                    disabled={isResetting}
                  >
                    {isResetting ? (
                      <>
                        <FiLoader className="stg-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <FiMail /> Send Reset Link
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button
                  className="stg-btn stg-btn-secondary"
                  onClick={() => setShowPasswordReset(true)}
                >
                  <FiRefreshCw /> Reset Password
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Session Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiLogOut /> Session
          </h3>
        </div>
        <div className="stg-card-body">
          <p style={{ color: "var(--stg-slate-600)", marginBottom: "16px" }}>
            Sign out of your account on this device. You can always sign back in
            with your credentials.
          </p>
          <button className="stg-btn stg-btn-secondary" onClick={handleLogout}>
            <FiLogOut /> Sign Out
          </button>
        </div>
      </motion.div>

      {/* Danger Zone Card */}
      <motion.div variants={itemVariants} className="stg-card stg-danger-zone">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiAlertTriangle /> Danger Zone
          </h3>
        </div>
        <div className="stg-card-body">
          <div
            className="stg-alert stg-alert-error"
            style={{ marginBottom: "16px" }}
          >
            <FiAlertTriangle className="stg-alert-icon" />
            <div className="stg-alert-content">
              <p className="stg-alert-title">Permanently Delete Account</p>
              <p className="stg-alert-message">
                Once you delete your account, there is no going back. All your
                data, posts, messages, and connections will be permanently
                removed.
              </p>
            </div>
          </div>

          {showDeleteConfirm ? (
            <div>
              {/* Password field - only for non-Google users */}
              {!isGoogleUser && (
                <div className="stg-form-group">
                  <label className="stg-form-label">
                    <FiLock /> Enter your password to confirm
                  </label>
                  <div className="stg-input-wrapper">
                    <input
                      type={showDeletePassword ? "text" : "password"}
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      className="stg-input"
                      placeholder="Your password"
                      style={{ paddingRight: "48px" }}
                    />
                    <button
                      type="button"
                      className="stg-input-action"
                      onClick={() => setShowDeletePassword(!showDeletePassword)}
                    >
                      {showDeletePassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>
              )}

              {/* Confirmation text field */}
              <div className="stg-form-group">
                <label className="stg-form-label">
                  <FiAlertTriangle /> Type <strong>DELETE_MY_ACCOUNT</strong> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="stg-input"
                  placeholder="DELETE_MY_ACCOUNT"
                />
              </div>

              <div className="stg-flex stg-gap-md">
                <button
                  className="stg-btn stg-btn-secondary"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletePassword("");
                    setDeleteConfirmText("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="stg-btn stg-btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={
                    isDeleting || 
                    deleteConfirmText !== "DELETE_MY_ACCOUNT" ||
                    (!isGoogleUser && !deletePassword)
                  }
                >
                  {isDeleting ? (
                    <>
                      <FiLoader className="stg-spin" /> Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 /> Delete My Account
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="stg-btn stg-btn-danger"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <FiTrash2 /> Delete Account
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AccountSettings;
